import { f7 } from 'framework7-react';
import II18 from '../components/translate/II18';
import { gaeApp } from '../components/helpers/Analytics';
import { saveDocuments } from '../redux/actions/rootAction';
import {
  saveForcedReloadSnapshot,
  takeForcedReloadSnapshot
} from './standaloneDraftCache';

/**
 * Keeps user input across a forced reload (stale build, see importWithRetry):
 * - /r= — intermediary storage only, nothing in the browser (PIN/OTP data);
 * - /t= — one-shot IndexedDB snapshot under its own key.
 * sessionStorage holds only a marker (no user data), so after the reload the
 * data is restored silently instead of asking "restore saved documents?".
 */
const MARKER_KEY = 'pwa-forced-reload';
const MARKER_TTL_MS = 15 * 60 * 1000;
const INTERMEDIARY_SAVE_TIMEOUT_MS = 5000;

export const FORCED_RELOAD_MODE = {
  REQUEST: 'r',
  TEMPLATE: 't'
};

let store = null;

export const initForcedReload = reduxStore => {
  store = reduxStore;
};

const writeMarker = marker => {
  try {
    window.sessionStorage.setItem(MARKER_KEY, JSON.stringify(marker));
  } catch (e) {
    // no marker → regular start after reload
  }
};

const readMarker = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(MARKER_KEY) || 'null');
  } catch (e) {
    return null;
  }
};

const clearMarker = () => {
  try {
    window.sessionStorage.removeItem(MARKER_KEY);
  } catch (e) {
    // ignore
  }
};

const hasUserData = state =>
  Boolean(
    (state.forms && state.forms.size) ||
      (state.scannedDocuments && state.scannedDocuments.size)
  );

const withTimeout = (promise, ms, fallback) =>
  Promise.race([
    promise,
    new Promise(resolve => setTimeout(() => resolve(fallback), ms))
  ]);

/**
 * @returns {Promise<{saved: boolean, lost: boolean}>}
 *   lost — there was user input, but it could not be kept
 */
export const saveBeforeForcedReload = async () => {
  const state = store && store.getState();
  if (
    !state ||
    !state.recipientRequestId ||
    state.scanEnded ||
    !hasUserData(state)
  ) {
    return { saved: false, lost: false };
  }
  const requestId = state.recipientRequestId;

  if (state.templateMode) {
    // After prepare the template became a request; a reload of /t= starts over.
    if (state.templateRequestPrepared) return { saved: false, lost: true };
    const saved = await saveForcedReloadSnapshot({
      templateId: requestId,
      forms: state.forms,
      scannedDocuments: state.scannedDocuments,
      documentsStatus: state.documentsStatus,
      signatures: state.signatures,
      successSentKeys: state.successSentKeys,
      rejectedDocuments: state.rejectedDocuments,
      usersData: state.usersData
    });
    if (saved) {
      writeMarker({
        requestId,
        mode: FORCED_RELOAD_MODE.TEMPLATE,
        at: Date.now()
      });
    }
    return { saved, lost: !saved };
  }

  if (!state.canSaveDocuments) return { saved: false, lost: true };
  const saved = await withTimeout(
    store.dispatch(
      saveDocuments(
        requestId,
        state.requestedDocuments,
        state.scannedDocuments,
        state.forms,
        state.documentsStatus
      )
    ),
    INTERMEDIARY_SAVE_TIMEOUT_MS,
    false
  );
  if (saved === true) {
    writeMarker({
      requestId,
      mode: FORCED_RELOAD_MODE.REQUEST,
      at: Date.now()
    });
  }
  return { saved: saved === true, lost: saved !== true };
};

/**
 * True once per forced reload of this request/template; clears the marker.
 * @param {string} requestId
 * @param {string} mode FORCED_RELOAD_MODE
 */
export const consumeForcedReloadMarker = (requestId, mode) => {
  const marker = readMarker();
  if (!marker) return false;
  if (!marker.at || Date.now() - marker.at > MARKER_TTL_MS) {
    clearMarker();
    return false;
  }
  if (marker.requestId !== requestId || marker.mode !== mode) return false;
  clearMarker();
  return true;
};

/**
 * @param {string} templateId
 * @returns {Promise<object|null>} draft state for receiveStandaloneDraft
 */
export const takeTemplateSnapshotAfterForcedReload = async templateId => {
  if (!consumeForcedReloadMarker(templateId, FORCED_RELOAD_MODE.TEMPLATE)) {
    return null;
  }
  const state = await takeForcedReloadSnapshot(templateId);
  gaeApp('FORCED_RELOAD_RESTORE', state ? 't:ok' : 't:miss');
  return state;
};

export const showDataRestoredToast = () => {
  f7.toast
    .create({
      text: II18.get('restart_app_data_restored'),
      cssClass: 'data-restored-toast',
      position: 'center',
      closeTimeout: 4000
    })
    .open();
};
